import React, {useEffect, useState} from 'react';

import {Button, Modal, Select} from '@/components';
import {Input, TextArea} from '@/components/common/Input';
import {
  getCorridorPairsOptions,
  getPaymentInstrumentsOptions,
  getProcessorsOptions,
} from '@/constants/formatters';

export type NotifyModalProps = {
  message?: string;
  processors: string[];
  selectedProcessor: string;
  corridorPairs: string[];
  selectedCorridorPair: string;
  paymentInstruments: string[];
  selectedPaymentInstrument: string;
  onSubmit: (data: NotifyModalData) => void;
  onClose: () => void;
  isOpen: boolean;
};

export type NotifyModalData = {
  title?: string;
  message: string;
  ttl: string;
  selectedProcessor: string;
  selectedCorridorPair?: string; // undefined if all
  selectedPaymentInstrument?: string; // undefined if all
};

const NotifyModal = ({
  message: initialMessage,
  selectedCorridorPair: corridorPair,
  selectedPaymentInstrument: paymentInstrument,
  selectedProcessor: processor,
  processors,
  corridorPairs,
  paymentInstruments,
  onSubmit,
  onClose,
  isOpen,
}: NotifyModalProps) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState(initialMessage || '');
  const [ttl, setTtl] = useState('2hrs');
  const [selectedProcessor, setSelectedProcessor] = useState(processor);
  const [selectedCorridorPair, setSelectedCorridorPair] =
    useState(corridorPair);
  const [selectedPaymentInstrument, setSelectedPaymentInstrument] =
    useState(paymentInstrument);

  // Update state when props change
  useEffect(() => {
    setMessage(initialMessage || '');
    setSelectedProcessor(processor);
    setSelectedCorridorPair(corridorPair);
    setSelectedPaymentInstrument(paymentInstrument);
  }, [initialMessage, processor, corridorPair, paymentInstrument]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // clean up
    const corridorPair =
      selectedCorridorPair === 'all' ? undefined : selectedCorridorPair;
    const paymentInstrument =
      selectedPaymentInstrument === 'all'
        ? undefined
        : selectedPaymentInstrument;

    onSubmit({
      title,
      message,
      ttl,
      selectedProcessor,
      selectedCorridorPair: corridorPair,
      selectedPaymentInstrument: paymentInstrument,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      closeOnOutsideClick
      title="Downtime Notification"
      zIndex={11}
    >
      <div className="container mx-auto p-4">
        <div className="z-20">
          <h6> Displayed as callout on mobile</h6>

          {/*Callout*/}
          <div className="items-center flex mb-3 mt-3">
            <div className="flex items-center rounded-lg border-2 border-gray-200 dark:border-0 px-4 py-3 text-sm w-full bg-yellow-100 dark:bg-yellow-800">
              <div className="">
                <div className="text-sm font-bold ">
                  <span className="mr-2">&#9888;</span>
                  {title}
                </div>
                <div className="mt-1 text-xs">{message}</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Corridor Pair Downtime"
            className="w-full"
            required
          />

          <TextArea
            label="Message"
            name="message"
            required
            onChange={(e) => setMessage(e.target.value)}
            value={message}
            aria-rowspan={4}
            type="text"
            className="w-full isFull"
          />

          <div className="gap-3 grid md:grid-cols-2">
            <Select
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedProcessor(e.target.value)
              }
              value={selectedProcessor}
              name="processor"
              options={getProcessorsOptions(processors)}
              placeholder="Select"
              label="Processor"
              className="md:w-full"
              required
            />

            <Select
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedCorridorPair(e.target.value)
              }
              value={selectedCorridorPair}
              name="corridorPair"
              options={getCorridorPairsOptions(corridorPairs)}
              placeholder="Select Corridor Pair"
              label="Corridor Pair"
              required
            />

            <Select
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedPaymentInstrument(e.target.value)
              }
              value={selectedPaymentInstrument}
              name="paymentInstrument"
              options={[
                {key: 'All', value: 'all'},
                ...getPaymentInstrumentsOptions(paymentInstruments),
              ]}
              placeholder="Select Payment Instrument"
              label="Payment Instrument"
              required
              className="w-full"
            />
          </div>

          <Select
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setTtl(e.target.value)
            }
            value={ttl}
            name="ttl"
            options={[
              {key: '2hrs', value: '2hrs'},
              {key: '1hr', value: '1hr'},
              {key: '30mins', value: '30mins'},
            ]}
            placeholder="Select"
            label="TTL: (Expires after)"
            required
          />

          <div className="flex justify-end mt-4">
            <Button type="submit" variant="solid" colorScheme="cyan">
              Create Notification
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default NotifyModal;
